import React, { useContext, useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { PlaceText } from "../components/PlaceText";
import { PlaceColors } from "../helpers/PlaceColors";
import { getTranslatedString } from "../helpers/FindifyTranslatedString";
import { MakeContentsLine } from "../helpers/ContentSlider";
import { getApiFromModule } from "../helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../helpers/DetailsHelpers";
import { useTranslation } from "react-i18next";

export type PageSection = {
    type: string;
    id: string;
    config: any;
};

type PageEditorRenderNativeProps = {
    sections: PageSection[];
    navigation: any;
};

export const FindifyPageEditorRenderNative = ({ sections, navigation }: PageEditorRenderNativeProps) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const { t } = useTranslation();
    const [contentsBySection, setContentsBySection] = useState<{ [key: string]: any[] }>({});

    useEffect(() => {
        sections.forEach((section) => {
            if (section.type === 'list') {
                loadContentsForList(section);
            }
        });
    }, [sections]);

    const loadContentsForList = async (section: PageSection) => {
        if (!findifyDataContext) return;

        const module = section.config?.preview_module || 'contents';
        const limit = (section.config?.items_per_row ?? 4) * (section.config?.number_of_rows ?? 1);
        const api = getApiFromModule(module, findifyDataContext);

        if (api) {
            let filters = {};
            let orderby = {};
            // Basic filtering/ordering if needed based on section.config
            
            api.getList(0, limit, null, filters, orderby).then((res: any) => {
                const items = res.results.map((item: any) => ({
                    ...item,
                    image: getFeaturedImageUrl(item),
                    subtitle: item.resume,
                    action: () => {
                        navigation.push('PlaceContentDetails', {
                            module: module,
                            content_id: item.content_id || item.event_id || item.poi_id || item.route_id,
                            page_slug: item.page_slug
                        });
                    }
                }));
                setContentsBySection(prev => ({ ...prev, [section.id]: items }));
            });
        }
    };

    const renderSection = (section: PageSection, index: number) => {
        switch (section.type) {
            case 'title':
                const titleText = getTranslatedString(section.config[`title_${findifyDataContext?.language || 'pt-PT'}`] || section.config.title, findifyDataContext);
                return (
                    <View key={section.id || index} style={styles.titleSection}>
                        <PlaceText style={[styles.titleText, { color: PlaceColors(findifyDataContext).text.get() }]}>
                            {titleText}
                        </PlaceText>
                    </View>
                );

            case 'list':
                const listTitle = getTranslatedString(section.config[`title_${findifyDataContext?.language || 'pt-PT'}`] || section.config.title, findifyDataContext) 
                                 || t('modules.content_types.' + (section.config?.preview_module || 'contents'));
                return (
                    <MakeContentsLine
                        key={section.id || index}
                        module={section.config?.preview_module || 'contents'}
                        language={findifyDataContext?.language || 'pt-PT'}
                        title={listTitle}
                        lIndex={index}
                        contents={contentsBySection[section.id] || []}
                        viewAllAction={() => {
                            navigation.push('PlaceContentList', {
                                module: section.config?.preview_module || 'contents',
                                ...section.config
                            });
                        }}
                    />
                );

            default:
                // Unsupported section type for native
                return null;
        }
    };

    return (
        <View style={styles.container}>
            {sections.map((section, index) => renderSection(section, index))}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
    },
    titleSection: {
        padding: 15,
        alignItems: 'center',
    },
    titleText: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
    },
});
